import React, { useEffect, useState } from 'react';
import { Loader2, FileDown, Save, FileSignature, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader } from '@/components/common/Feedback';
import { PricingBadge } from '@/components/opportunities/OppBadges';
import api from '@/lib/api';
import { fmtAmount } from '@/lib/format';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

const FIELD = 'space-y-1.5';

// Editable Change Order draft. The AI pre-fills every section; the PM can edit
// any of it before downloading the professionally formatted PDF.
export const ChangeOrderDialog = ({ opportunityId, open, onClose, acknowledged = false }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [co, setCo] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !opportunityId) { setCo(null); setForm(null); return; }
    setLoading(true);
    api.post(`/opportunities/${opportunityId}/change-order`, { acknowledged })
      .then((r) => { setCo(r.data); setForm(fromCo(r.data)); })
      .catch((e) => {
        const det = e?.response?.data?.detail;
        const msg = typeof det === 'string' ? det : (det?.blockers ? `${det.message}: ${det.blockers.join(' ')}` : 'Could not draft the change order');
        toast.error(msg); onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, opportunityId]);

  const fromCo = (c) => ({
    title: c.title || '',
    description: c.description || '',
    reason: c.reason || '',
    contractual_basis: c.contractual_basis || '',
    justification: c.justification || '',
    prepared_by: c.prepared_by || '',
    time_extension_days: c.time_extension_days ?? 0,
    clause_refs: (c.clause_refs || []).join(', '),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const persist = async () => {
    const payload = {
      title: form.title,
      description: form.description,
      reason: form.reason,
      contractual_basis: form.contractual_basis,
      justification: form.justification,
      prepared_by: form.prepared_by,
      time_extension_days: parseInt(form.time_extension_days, 10) || 0,
      clause_refs: form.clause_refs.split(',').map((s) => s.trim()).filter(Boolean),
    };
    const r = await api.patch(`/change-orders/${co.id}`, payload);
    setCo(r.data);
    return r.data;
  };

  const save = async () => {
    setSaving(true);
    try { await persist(); toast.success('Change order saved'); }
    catch (e) { toast.error('Could not save changes'); }
    finally { setSaving(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const updated = await persist(); // save edits first so the PDF matches
      const r = await api.post(`/change-orders/${co.id}/export`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${updated.co_number || 'change-order'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Change Order PDF downloaded');
      track('change_order_generated', { co_number: updated.co_number });
    } catch (e) { toast.error('Could not generate the change order PDF'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="change-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-4 w-4" />
            Change Order {co?.co_number ? <span className="text-slate-400 font-mono text-sm">&middot; {co.co_number}</span> : null}
          </DialogTitle>
        </DialogHeader>

        {loading || !form ? (
          <div className="py-10"><Loader label="Drafting change order with AI..." /></div>
        ) : (
          <>
            <p className="text-xs text-slate-500 -mt-1">Everything below is editable. Refine it, then download the formatted PDF.</p>
            <div className="space-y-4 py-1">
              <div className={FIELD}>
                <Label className="text-xs">Title</Label>
                <Input value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="co-title" />
              </div>
              <div className={FIELD}>
                <Label className="text-xs">Description of changed work</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="co-description" />
              </div>
              <div className={FIELD}>
                <Label className="text-xs">Reason for the change</Label>
                <Textarea rows={3} value={form.reason} onChange={(e) => set('reason', e.target.value)} data-testid="co-reason" />
              </div>
              <div className={FIELD}>
                <Label className="text-xs">Contractual basis</Label>
                <Textarea rows={3} value={form.contractual_basis} onChange={(e) => set('contractual_basis', e.target.value)} data-testid="co-contractual-basis" />
              </div>
              <div className={FIELD}>
                <Label className="text-xs">AI-generated justification</Label>
                <Textarea rows={4} value={form.justification} onChange={(e) => set('justification', e.target.value)} data-testid="co-justification" />
              </div>
              <div className={FIELD}>
                <Label className="text-xs">Contractual references (comma-separated)</Label>
                <Input value={form.clause_refs} onChange={(e) => set('clause_refs', e.target.value)} placeholder="e.g. Clause 14.3, Section 7.1" data-testid="co-clauses" />
              </div>

              <div className={FIELD}>
                <Label className="text-xs">Time impact (days)</Label>
                <Input type="number" min="0" value={form.time_extension_days} onChange={(e) => set('time_extension_days', e.target.value)} data-testid="co-time-days" className="max-w-[160px]" />
              </div>

              {/* Deterministic totals — the SAME shared calculation as the opportunity & notice */}
              {(() => {
                const calc = co?.calculation || {};
                const bd = calc.breakdown || {};
                const cur = calc.currency || 'USD';
                const ps = co?.pricing_status || calc.pricing_status || 'unquantified';
                const full = ps === 'fully_priced';
                return (
                  <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="co-calc-summary">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                      <span className="text-xs font-semibold text-slate-700">Calculation (shared with the notice)</span>
                      <PricingBadge status={ps} testid="co-pricing-status" />
                    </div>
                    {!full && (
                      <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex gap-2" data-testid="co-incomplete-warning">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-amber-800">Labelled <b>{ps === 'partially_priced' ? 'Partially priced' : 'Unquantified'}</b> — incomplete items are shown as &ldquo;financial impact to be substantiated&rdquo; and no partial figure is presented as the final total.</p>
                      </div>
                    )}
                    <div className="px-4 py-3 text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Base subtotal</span><span className="font-mono">{fmtAmount(bd.base_subtotal, cur)}</span></div>
                      {calc.tender_adjustment?.applied && (
                        <div className="flex justify-between"><span className="text-slate-500">Tender adjustment (base-rate lines)</span><span className="font-mono">{fmtAmount(bd.tender_amount, cur)}</span></div>
                      )}
                      <div className="flex justify-between"><span className="text-slate-500">Markups</span><span className="font-mono">{fmtAmount(bd.markup_total, cur)}</span></div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-slate-200 font-semibold">
                        <span>Final amount</span>
                        {full
                          ? <span className="font-mono text-money-700" data-testid="co-total">{fmtAmount(calc.final_amount, cur)}</span>
                          : <span className="text-amber-600 text-xs" data-testid="co-total">To be substantiated</span>}
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className={FIELD}>
                <Label className="text-xs">Prepared by</Label>
                <Input value={form.prepared_by} onChange={(e) => set('prepared_by', e.target.value)} data-testid="co-prepared-by" />
              </div>

              <p className="text-[11px] text-slate-400">Amounts come only from the shared deterministic calculation on the opportunity — the same numbers as the notice. Edit line items, quantities, rates, tender and markups in the opportunity's calculator.</p>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={save} disabled={saving || downloading} data-testid="co-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
              </Button>
              <Button onClick={download} disabled={downloading || saving} className="bg-money hover:bg-money-700" data-testid="co-download-btn">
                {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}Download PDF
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
